;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/admin/tenants/:id\",\"regex\":\"^/admin/tenants/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/admin\"},{\"path\":\"/admin/tenants\"},{\"path\":\"/admin/users\"},{\"path\":\"/auth/forgot\"},{\"path\":\"/auth/login\"},{\"path\":\"/auth/register\"},{\"path\":\"/auth/reset-password\"},{\"path\":\"/blank\"},{\"path\":\"/dashboard\"},{\"path\":\"/dashboard-cms\"},{\"path\":\"/dashboard-cms/app\"},{\"path\":\"/dashboard-cms/cupons\"},{\"path\":\"/dashboard-cms/equipe/membros\"},{\"path\":\"/dashboard-cms/ferramentas/telegram\"},{\"path\":\"/dashboard-cms/lojas\"},{\"path\":\"/dashboard-cms/produtos\"},{\"path\":\"/dashboard-cms/realtime\"},{\"path\":\"/dashboard-cms/saude\"},{\"path\":\"/dashboard-cms/spy\"},{\"path\":\"/dashboard-saas\"},{\"path\":\"/plugins\"},{\"path\":\"/plugins/allsite-seo/settings\"},{\"path\":\"/plugins/super-cache/settings\"},{\"path\":\"/settings\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.2.6";import * as Sentry from "@sentry/nextjs"

// Known noisy errors we deliberately suppress (regex-matched against
// the error message). They are either bugs inside the Next.js devtools
// overlay itself or browser quirks we can't fix from app code:
//
// - releasePointerCapture / NotFoundError: Next 16 devtools tries to
//   release pointer capture on Radix UI triggers after a theme toggle
//   re-render unmounts them. Pure devtools internal, no app impact.
// - ResizeObserver loop ... delivered: noise from layout libs.
// - Load failed (ipapi.co / open-meteo): optional geo/weather, we
//   already swallow it silently in dashboard-header.tsx.
const SUPPRESS_PATTERNS = [
  /releasePointerCapture/i,
  /NotFoundError: The object can not be found here/i,
  /ResizeObserver loop/i,
  /Load failed.*ipapi\.co/i,
  /Load failed.*open-meteo/i,
]

function shouldSuppress(message: string | undefined | null): boolean {
  if (!message) return false
  return SUPPRESS_PATTERNS.some((re) => re.test(message))
}

if (typeof window !== "undefined") {
  // Root-cause patch: Element.releasePointerCapture throws
  // NotFoundError when the pointer is not currently captured. The
  // Next 16 devtools bundle calls it after Radix UI re-renders
  // (e.g. after a theme toggle) and the throw is what the overlay
  // catches as "1 issue". Wrapping the native method silently
  // ignores the NotFoundError and leaves real errors untouched.
  if (typeof Element !== "undefined" && Element.prototype.releasePointerCapture) {
    const originalRelease = Element.prototype.releasePointerCapture
    Element.prototype.releasePointerCapture = function (pointerId: number) {
      try {
        return originalRelease.call(this, pointerId)
      } catch (err) {
        if (err instanceof DOMException && err.name === "NotFoundError") return
        throw err
      }
    }
  }

  // Belt-and-suspenders for everything else in SUPPRESS_PATTERNS: stop
  // the event from reaching the Next dev overlay + Sentry.
  window.addEventListener(
    "error",
    (event) => {
      const msg = String(event.error?.message ?? event.message ?? "")
      if (shouldSuppress(msg)) {
        event.preventDefault()
        event.stopImmediatePropagation()
      }
    },
    true,
  )
  window.addEventListener(
    "unhandledrejection",
    (event) => {
      const reason = event.reason
      const msg = typeof reason === "string" ? reason : String(reason?.message ?? "")
      if (shouldSuppress(msg)) {
        event.preventDefault()
      }
    },
    true,
  )
}

Sentry.init({
  dsn:
    process.env.NEXT_PUBLIC_SENTRY_DSN ||
    "https://950bb8adc873d198ebee7c86af82e932@o4511361202388992.ingest.us.sentry.io/4511397248040960",

  // Adds request headers and IP for users (useful when correlating
  // a feedback report or error to a specific tenant user).
  sendDefaultPii: true,

  // 100% in dev, 10% in prod. Lower this if traffic grows.
  tracesSampleRate: process.env.NODE_ENV === "development" ? 1.0 : 0.1,

  // Drop noisy events that match SUPPRESS_PATTERNS before they hit Sentry.
  beforeSend(event, hint) {
    const original = hint?.originalException
    const msg =
      (typeof original === "string" ? original : (original as Error | undefined)?.message) ||
      event.message ||
      ""
    if (shouldSuppress(msg)) return null
    return event
  },

  integrations: [
    // User Feedback widget — embedded "Report a Bug" button bottom-right.
    Sentry.feedbackIntegration({
      colorScheme: "system",
      showBranding: false,
      triggerLabel: "Reportar um bug",
      formTitle: "Reportar um bug",
      submitButtonLabel: "Enviar relato",
      cancelButtonLabel: "Cancelar",
      confirmButtonLabel: "Confirmar",
      addScreenshotButtonLabel: "Anexar screenshot",
      removeScreenshotButtonLabel: "Remover screenshot",
      nameLabel: "Nome",
      namePlaceholder: "Seu nome",
      emailLabel: "E-mail",
      emailPlaceholder: "voce@exemplo.com",
      isRequiredLabel: "(obrigatório)",
      messageLabel: "Descrição",
      messagePlaceholder: "Qual é o bug? O que você esperava?",
      successMessageText: "Obrigado pelo relato!",
    }),
  ],

  // Match envs in Sentry to filter issues.
  environment: process.env.NODE_ENV,
})

// Instruments App Router navigations as Sentry transactions.
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart
